import React from "react";
import {
  Box,
  Button,
  Container,
  Header,
  Link,
  Popover,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationOtherOwner,
  RelatedInvestigation,
  Stage,
} from "../../lusca-search-api/generated-src";
import { InvestigationFamilyLinks } from "./InvestigationFamilyLinks";
import { TrendSparkline } from "./TrendSparkline";
import { formatImpactUsd } from "./InvestigationTable";
import {
  formatRelativeTime,
  formatSnapshotTimestamp,
  investigationPath,
  isTerminal,
  latestSnapshotDate,
} from "./investigationUtils";

/**
 * The population in three numbers that agree with each other and with the Items tab:
 * how many items the case holds, where split-off items went, and whether it is shrinking.
 */
export interface PopulationFacts {
  /** Items the case holds: its frozen manifest (owned + free) once trimmed. */
  items: number;
  /** Items the rule matched that now sit with other cases (0 when none). */
  moved: number;
  /** Where they went, when known (after a trim). */
  movedTo: InvestigationOtherOwner[];
  /** Every moved item went to a child of this case. */
  allChildren: boolean;
  assigning: boolean;
}

export const populationFacts = (
  investigation: Investigation
): PopulationFacts => {
  const population = investigation.population ?? {};
  const count = population.count ?? 0;
  const assigning = investigation.assignment?.status === "PENDING";
  const movedTo = population.movedTo ?? [];
  const trimmedMoved = movedTo.reduce((sum, o) => sum + (o.count ?? 0), 0);
  // Before the hourly trim reaches an older case its manifest still lists items other cases
  // own: count what it owns, so the headline is right from the first render.
  const untrimmed =
    !assigning && !isTerminal(investigation.status)
      ? Math.max(0, count - (investigation.assignedCount ?? count))
      : 0;
  const children = new Set(investigation.childInvestigationIds ?? []);
  const allChildren =
    movedTo.length > 0
      ? movedTo.every(
          (o) => o.relation === "child" || children.has(o.investigationId)
        )
      : untrimmed > 0 && children.size > 0 && !population.trimmedAt;
  // An active case headlines what it owns now. The hourly reconcile claims newly matching items
  // without growing the frozen manifest, so `assignedCount` can exceed `count`; below it, the
  // gap is what moved elsewhere (counted above), so this is the same number either way.
  const items =
    !assigning && !isTerminal(investigation.status)
      ? investigation.assignedCount ?? count
      : count;
  return {
    items,
    moved: trimmedMoved + untrimmed,
    movedTo,
    allChildren,
    assigning,
  };
};

const MovedToList: React.FC<{
  movedTo: InvestigationOtherOwner[];
  stage: Stage;
}> = ({ movedTo, stage }) => (
  <SpaceBetween size="xxs">
    {movedTo.map((o) => (
      <Box key={o.investigationId} fontSize="body-s">
        <Link href={investigationPath(o.investigationId, stage)} external>
          {o.title || o.investigationId}
        </Link>{" "}
        · {o.count.toLocaleString()}
        {o.relation === "child" ? " (child)" : ""}
      </Box>
    ))}
  </SpaceBetween>
);

interface PopulationFact {
  label: string;
  value: React.ReactNode;
}

interface InvestigationPopulationCardProps {
  investigation: Investigation;
  stage: Stage;
  /** Opens the tab with the full trend chart. */
  onViewTrend: () => void;
  /** Rendered above the numbers (the owner field). */
  top?: React.ReactNode;
  /** Rendered below the numbers (created / last activity). */
  bottom?: React.ReactNode;
  /** Opens the Related tab (from "N more" children). */
  onViewRelated?: () => void;
  /** Related cases with titles (InvestigationDetail.related). */
  related?: RelatedInvestigation[] | null;
}

export const InvestigationPopulationCard: React.FC<
  InvestigationPopulationCardProps
> = ({
  investigation,
  stage,
  onViewTrend,
  top,
  bottom,
  onViewRelated,
  related,
}) => {
  const facts = populationFacts(investigation);
  const snapshotDate = latestSnapshotDate(investigation);
  const frozenFrom = investigation.population?.snapshotTimestamp;
  const snapshotTitle = [
    `DLQ snapshot ${snapshotDate ? snapshotDate.toLocaleString() : "unknown"}`,
    frozenFrom
      ? `population frozen from ${formatSnapshotTimestamp(frozenFrom)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const items: PopulationFact[] = [
    {
      label: "Items",
      value: (
        <SpaceBetween size="xxxs">
          <Box
            fontSize="heading-xl"
            fontWeight="bold"
            data-testid="population-items"
          >
            {facts.items.toLocaleString()}
          </Box>
          {facts.assigning && (
            <Box color="text-body-secondary" fontSize="body-s">
              Assigning ownership…
            </Box>
          )}
        </SpaceBetween>
      ),
    },
    {
      label: "Impact",
      value:
        investigation.impactUsd === undefined ||
        investigation.impactUsd === null ? (
          // Filled in by the hourly reconcile; a new case has none until then.
          <Box color="text-body-secondary" data-testid="population-impact">
            Awaiting reconcile
          </Box>
        ) : (
          <Box
            fontSize="heading-xl"
            fontWeight="bold"
            data-testid="population-impact"
          >
            {formatImpactUsd(investigation.impactUsd)}
          </Box>
        ),
    },
  ];
  // Distinct orders among the owned items. Absent until a reconcile (or creation) counts them,
  // and not computed for oversized cases, so the column is left out rather than shown as 0.
  const orderCount = investigation.orderCount;
  if (
    orderCount !== undefined &&
    orderCount !== null &&
    !investigation.populationOversized
  ) {
    items.push({
      label: "Distinct orders",
      value: (
        <Box
          fontSize="heading-xl"
          fontWeight="bold"
          data-testid="population-orders"
        >
          {orderCount.toLocaleString()}
        </Box>
      ),
    });
  }
  // The headline numbers keep their width so a number never breaks across lines.
  const headlineCount = items.length;
  if (facts.moved > 0) {
    const label = facts.allChildren ? "Split off" : "Moved to other cases";
    const count = facts.moved.toLocaleString();
    items.push({
      label,
      value:
        facts.movedTo.length > 0 ? (
          <Popover
            header={label}
            size="medium"
            triggerType="text"
            content={<MovedToList movedTo={facts.movedTo} stage={stage} />}
          >
            <span data-testid="population-moved">{count}</span>
          </Popover>
        ) : (
          <span data-testid="population-moved">{count}</span>
        ),
    });
  }

  items.push({
    label: "Last 7 days",
    value: (
      <SpaceBetween size="xxxs">
        <TrendSparkline trend={investigation.trend} />
        <Button variant="inline-link" onClick={onViewTrend}>
          View trend
        </Button>
      </SpaceBetween>
    ),
  });

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={
            <span title={snapshotTitle}>
              Snapshot {formatRelativeTime(snapshotDate?.toISOString())}
            </span>
          }
        >
          Details
        </Header>
      }
    >
      <SpaceBetween size="l">
        {top}
        {/*
          One row that never wraps: KeyValuePairs reflows to two columns at this width, which
          made the card (and so the stretched Overview beside it) taller.
        */}
        <div
          style={{
            display: "flex",
            flexWrap: "nowrap",
            gap: "32px",
            alignItems: "flex-start",
          }}
        >
          {items.map((item, index) => (
            // Items, Impact and Distinct orders keep their width so a number never breaks across
            // lines; the later columns shrink instead (their labels wrap).
            <div
              key={String(item.label)}
              style={
                index < headlineCount ? { flexShrink: 0 } : { minWidth: 0 }
              }
            >
              <Box variant="awsui-key-label">{item.label}</Box>
              <div
                style={
                  index < headlineCount ? { whiteSpace: "nowrap" } : undefined
                }
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>
        <InvestigationFamilyLinks
          investigation={investigation}
          stage={stage}
          related={related}
          onViewRelated={onViewRelated}
        />
        {bottom}
      </SpaceBetween>
    </Container>
  );
};

export default InvestigationPopulationCard;
